import { Icon, TextArea } from '@blueprintjs/core';
import BOSDateInput from 'Components/Generic/BOSDateInput';
import VendorSelect from 'Components/Generic/BOSFilterSelect/VendorSelect';
import BOSInput from 'Components/Generic/BOSInput';
import { showSaveSuccessToast } from 'Components/Generic/BOSToasterUtil';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import ServiceContractDrawerButton from 'Components/SiteDrawerContainer/ServiceContractDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import ServiceContractData from 'Data/ServiceContractData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useValidation } from 'Lib/hooks/useValidation';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { formatMoney } from 'Util';
import { formatDate } from 'Util/dateUtil';

let DM = new DataManager();

const ServiceContractInformationPanel = ({
  serviceContractId,
  serviceContractObject,
  title = 'Service Contract',
  editable = false,
  collapsible = true,
  linkToServiceContractPage = false,
  ...props
}) => {
  const {
    currentObject: serviceContract,
    hasBeenUpdated,
    reset: resetServiceContract,
    updateField,
    originalObject: originalServiceContract,
    doSave,
  } = useUpdatableObject(
    ServiceContractData.updateRecord,
    ServiceContractData.createRecord,
  );
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalServiceContract,
    undoFunction: ServiceContractData.updateRecord,
    onUndo: (originalServiceContract) =>
      fetchServiceContract(originalServiceContract.id),
    objectType: 'Service Contract',
  });

  const [editState, setEditState] = useState(undefined);

  useEffect(() => {
    DM.add(
      ServiceContractData.registerListener(
        'BLANK_RECORD_LOADED',
        onServiceContractLoaded,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_LOADED',
        onServiceContractLoaded,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_CREATED',
        onServiceContractCreated,
      ),
      ServiceContractData,
    );
    DM.add(
      ServiceContractData.registerListener(
        'RECORD_UPDATED',
        onServiceContractUpdated,
      ),
      ServiceContractData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  let fields = {
    name: {
      label: 'Name',
      value: linkToServiceContractPage ? (
        <ServiceContractDrawerButton
          serviceContractId={serviceContract.id}
          label={serviceContract.name}
        />
      ) : (
        serviceContract.name
      ),
      onEdit: (newValue) => {
        updateField('name', newValue);
      },
      isRequired: true,
    },

    organizations: {
      label: 'Core Facility',
      isRequired: false,
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <div style={{ fontStyle: 'italic', color: 'var(--bp-gray3)' }}>
              Link/Unlink Core Facilities Below
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                gap: 2,
              }}>
              {serviceContract.service_contract_details?.length > 0 &&
                serviceContract.service_contract_details
                  .map((detail) => detail.organization)
                  .filter(
                    (obj1, i, arr) =>
                      arr.findIndex((obj2) => obj2.id === obj1.id) === i,
                  )
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((org, index, arr) => (
                    <Fragment key={index}>
                      <OrgNameWithDrawerButton orgId={org.id} />
                      {index < arr.length - 1 && ' | '}
                    </Fragment>
                  ))}
            </div>
          );
        },
      },
    },

    vendor_id: {
      label: 'Vendor',
      isRequired: true,
      isNumber: true,
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <div>
              <VendorSelect
                isRequired
                defaultSelectedId={serviceContract.vendor_id}
                onSelectVendor={(vendor) =>
                  updateField('vendor_id', vendor?.id || null)
                }
                onBlur={blurField}
              />
            </div>
          ) : (
            <VendorNameWithDrawerButton vendorId={serviceContract.vendor_id} />
          );
        },
      },
    },

    // user_id: {
    //   label: 'User',
    //   isRequired: false,
    //   isNumber: true,
    //   value: {
    //     render: (inEditState, blurField) => {
    //       return inEditState ? (
    //         <UserSelect
    //           defaultSelectedId={serviceContract.user_id}
    //           onSelectUser={(user) => updateField('user_id', user?.id || null)}
    //           onBlur={blurField}
    //         />
    //       ) : (
    //         <span>{serviceContract.user?.full_name}</span>
    //       );
    //     },
    //   },
    // },

    start_date: {
      label: 'Valid From',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSDateInput
              value={serviceContract?.start_date}
              onChange={(newDate) => {
                updateField('start_date', newDate);
              }}
            />
          ) : (
            formatDate(serviceContract?.start_date)
          );
        },
      },
    },

    end_date: {
      label: 'Valid To',
      // isRequired: true,
      // fieldSchema: joi.string().required().messages({
      //   'any.required': 'End Date is required',
      //   'string.base': 'End Date is required',
      // }),
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <BOSDateInput
              value={serviceContract?.end_date}
              onChange={(newDate) => updateField('end_date', newDate)}
              onBlur={blurField}
            />
          ) : (
            formatDate(serviceContract.end_date)
          );
        },
      },
    },

    activity: {
      label: 'Activity',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSInput
              showClear
              value={serviceContract.details?.activity}
              onChange={(newValue) => {
                updateField('details', {
                  ...serviceContract.details,
                  activity: newValue,
                });
              }}
            />
          ) : (
            serviceContract.details?.activity
          );
        },
      },
    },

    po_number: {
      label: 'PO Number',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSInput
              showClear
              value={serviceContract.details?.po_number}
              onChange={(newValue) => {
                updateField('details', {
                  ...serviceContract.details,
                  po_number: newValue,
                });
              }}
            />
          ) : (
            serviceContract.details?.po_number
          );
        },
      },
    },

    total_amount: {
      label: 'Total Amount',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSInput
              isMoneyInput
              showClear
              value={serviceContract.details?.total_amount}
              onChange={(newValue) => {
                updateField('details', {
                  ...serviceContract.details,
                  total_amount: newValue,
                });
              }}
            />
          ) : (
            `$${formatMoney(serviceContract.details?.total_amount)}`
          );
        },
      },
    },

    payment_arrangements: {
      label: 'Payment Arrangements',
      value: serviceContract.details?.payment_arrangements,
      onEdit: (newValue) => {
        updateField('details', {
          ...serviceContract.details,
          payment_arrangements: newValue,
        });
      },
    },

    date_paid: {
      label: 'Date Paid',
      value: {
        render: (inEditState, blurField) => {
          return inEditState ? (
            <BOSDateInput
              value={serviceContract.details?.date_paid}
              onChange={(newDate) =>
                updateField('details', {
                  ...serviceContract.details,
                  date_paid: newDate,
                })
              }
            />
          ) : (
            formatDate(serviceContract.details?.date_paid)
          );
        },
      },
    },

    amount_owed: {
      label: 'Amount Owed',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <BOSInput
              isMoneyInput
              showClear
              value={serviceContract.details?.amount_owed}
              onChange={(newValue) => {
                updateField('details', {
                  ...serviceContract.details,
                  amount_owed: newValue,
                });
              }}
            />
          ) : (
            `$${formatMoney(serviceContract.details?.amount_owed)}`
          );
        },
      },
    },

    up_for_renewal: {
      label: 'Up for Renewal',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <div style={{ display: 'flex' }}>
              <input
                type='checkbox'
                checked={serviceContract.details?.up_for_renewal}
                onChange={(event) => {
                  updateField('details', {
                    ...serviceContract.details,
                    up_for_renewal: event.target.checked,
                  });
                }}
              />
            </div>
          ) : (
            <Icon
              size='20'
              icon={serviceContract.details?.up_for_renewal ? 'tick' : 'blank'}
            />
          );
        },
      },
    },

    description: {
      label: 'Comments',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <TextArea
              fill
              autoResize
              value={serviceContract.description || ''}
              onChange={(e) => {
                updateField('description', e.target.value);
              }}
            />
          ) : (
            <div
              style={{
                whiteSpace: 'pre-line',
              }}>
              {serviceContract.description}
            </div>
          );
        },
      },
    },

    // details: {
    //   label: 'Details',
    //   onEdit: (newValue) => {
    //     updateField('details', newValue);
    //   },
    // },
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, serviceContract);

  useEffect(() => {
    if (serviceContractId && serviceContractId >= 0) {
      fetchServiceContract(serviceContractId);
    }
  }, [serviceContractId]);

  useEffect(() => {
    if (!editable && serviceContractObject) {
      resetServiceContract(serviceContractObject);
    } else if (
      serviceContractObject?.id &&
      Number(serviceContractObject.id) >= 0
    ) {
      fetchServiceContract(serviceContractObject.id);
    }
  }, [serviceContractObject]);

  const onServiceContractLoaded = (payload) => {
    if (payload) {
      resetServiceContract(payload);
      resetUndo(() => fetchServiceContract(payload.id));
      resetValidation();
    }
  };

  const fetchServiceContract = (id) => {
    ServiceContractData.getServiceContractAndAssociations(id);
  };

  const onServiceContractCreated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Service Contract Created');
      fetchServiceContract(payload.id);
      setEditState(false);
    }
  };

  const onServiceContractUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
      setEditState(false);
    }
  };
  const undoAction = () => {
    fetchServiceContract(serviceContract.id);
    resetValidation();
  };

  const undoButton = editable && hasBeenUpdated && (
    <UndoButton clickHandler={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    <EditableDetailPanel
      title={title}
      footer={footer}
      editable={editable}
      collapsible={collapsible}
      renderChild={getValidatedDetailTable}
      parentEditState={props?.parentEditState || editState}
      {...props}
    />
  );
};

ServiceContractInformationPanel.propTypes = {
  serviceContractId: PropTypes.number,
  serviceContractObject: PropTypes.object,
  title: PropTypes.string,
  editable: PropTypes.bool,
  collapsible: PropTypes.bool,
  linkToServiceContractPage: PropTypes.bool,
};

export default ServiceContractInformationPanel;
